import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const FAQS = [
  {
    q: "Combien de temps faut-il pour avoir mon site en ligne ?",
    a: "Comptez 5 jours ouvrés pour la formule Starter, 10 jours pour la formule Pro, et un planning personnalisé pour la formule Premium. Le délai démarre dès réception de vos textes et photos : plus vous êtes prêt, plus c'est rapide.",
  },
  {
    q: "L'hébergement et le nom de domaine sont-ils inclus ?",
    a: "La mise en ligne est incluse dans chaque formule. Pour l'hébergement, deux choix : l'abonnement Maintenance + Hébergement (14,99 €/mois pendant 3 mois, puis bascule automatique à 35,99 €/an, soit 2,99 €/mois) qui inclut aussi vos modifications à la demande, ou l'abonnement Hébergement seul à 26,99 €/an. Dans les deux cas, votre site est hébergé chez OVHcloud, hébergeur français. Si vous possédez déjà un nom de domaine, nous le raccordons gratuitement ; sinon nous vous aidons à le réserver à votre nom.",
  },
  {
    q: "Pourrai-je modifier mon site moi-même ensuite ?",
    a: "Oui. À la livraison, nous vous montrons comment changer vos textes, photos et horaires en quelques clics (formation express incluse dès la formule Pro). Et si vous préférez déléguer, l'abonnement Maintenance + Hébergement (2,99 €/mois en rythme annuel) prend en charge vos modifications à la demande.",
  },
  {
    q: "Comment se passe le paiement ?",
    a: "Le paiement est sécurisé par Stripe (carte bancaire). Vous pouvez régler directement en ligne depuis la carte de l'offre, ou demander d'abord un devis gratuit : rien ne démarre sans votre accord écrit. Les prix affichés sont nets, sans frais cachés.",
  },
  {
    q: "Le site m'appartient-il vraiment ?",
    a: "Entièrement. Vous êtes propriétaire de votre site, de vos contenus et de votre nom de domaine. Aucun abonnement obligatoire : vous restez libre de le faire évoluer avec nous ou avec quelqu'un d'autre.",
  },
  {
    q: "Mon site sera-t-il visible sur Google ?",
    a: "Chaque site est construit avec les bonnes bases techniques (rapidité, mobile, balises) et déclaré à Google. La formule Pro ajoute l'optimisation locale (Google Maps, mots-clés de votre ville) et la Premium une stratégie SEO complète avec rapport mensuel.",
  },
  {
    q: "Je n'ai ni photos ni textes, est-ce un problème ?",
    a: "Non. Nous rédigeons vos textes avec vous à partir d'un simple échange téléphonique et sélectionnons des visuels professionnels adaptés à votre métier. Un shooting ou un logo peuvent être ajoutés en option.",
  },
  {
    q: "Que se passe-t-il si je veux résilier l'abonnement Maintenance + Hébergement ?",
    a: "L'abonnement est sans engagement : un appel ou un email suffit, la résiliation prend effet à la fin de la période déjà payée et vous récupérez votre site tel quel pour l'héberger où vous voulez.",
  },
];

const FaqItem = ({ item, i, open, onToggle }) => (
  <div className={`rounded-3xl border transition-colors ${open ? "border-gold/40 bg-white" : "border-navy/10 bg-white/60 hover:border-gold/30"}`}>
    <button
      data-testid={`faq-question-${i}`}
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
    >
      <span className="font-display text-base font-bold text-navy sm:text-lg">{item.q}</span>
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${open ? "rotate-180 bg-navy text-gold" : "bg-gold/15 text-gold-dark"}`}>
        <ChevronDown size={16} />
      </span>
    </button>
    <div className={`grid transition-all duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
      <div className="overflow-hidden">
        <p className="px-6 pb-6 text-sm leading-relaxed text-slate-600" data-testid={`faq-answer-${i}`}>{item.a}</p>
      </div>
    </div>
  </div>
);

export const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="border-t border-navy/10 py-16 sm:py-24 lg:py-32" data-testid="faq-section">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-4">
          <Reveal>
            <Eyebrow>Questions fréquentes</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Tout ce que vous voulez savoir avant de vous lancer.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Une question qui n'est pas ici ? Appelez-nous au{" "}
              <a href="tel:+33784678802" className="font-semibold text-navy underline-offset-4 hover:text-gold-dark hover:underline" data-testid="faq-phone-link">07 84 67 88 02</a>, nous répondons sans jargon.
            </p>
          </Reveal>
        </div>
        <div className="space-y-3 lg:col-span-8" data-testid="faq-list">
          {FAQS.map((item, i) => (
            <Reveal key={item.q} delay={i * 0.05}>
              <FaqItem item={item} i={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
